import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { contentAPI } from '../../utils/api';
import { openReservation } from '../../utils/reservations';
import { track } from '../../utils/analytics';
import InquiryForm from './InquiryForm';

const Icon = ({ name, size = 36, className = '' }) => {
  const Cmp = LucideIcons[name];
  if (!Cmp) return null;
  return <Cmp size={size} className={className} />;
};

const RichText = ({ data }) => (
  <div className="max-w-4xl mx-auto px-6">
    {data.title && (
      <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
        {data.title}
      </h2>
    )}
    <div className="prose prose-lg max-w-none">
      {(data.paragraphs || []).map((p, idx) => (
        <p key={idx} className="text-lg leading-relaxed mb-6 text-gray-800">
          {p}
        </p>
      ))}
    </div>
  </div>
);

const CardWithImage = ({ data }) => (
  <div className="max-w-4xl mx-auto px-6">
    <div className="bg-gradient-to-br from-[#F5EBE0] to-[#E8C9A0] rounded-2xl p-8 md:p-12 shadow-xl border-l-4 border-[#C9A86C]">
      <div className={`grid ${data.image_url ? 'md:grid-cols-3' : 'md:grid-cols-1'} gap-8 items-start`}>
        {data.image_url && (
          <div className="md:col-span-1">
            <img src={data.image_url} alt={data.name || ''} className="w-full h-64 object-cover rounded-xl shadow-lg" />
          </div>
        )}
        <div className={data.image_url ? 'md:col-span-2' : ''}>
          {data.name && (
            <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
              {data.name}
            </h3>
          )}
          {data.title && <p className="text-[#B8956A] font-semibold text-lg mb-4">{data.title}</p>}
          <div className="space-y-4 text-gray-800">
            {(data.paragraphs || []).map((p, idx) => (
              <p key={idx} className="leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </div>
      {(data.footer_label || data.footer_body) && (
        <div className="mt-10">
          <p className="font-semibold text-gray-900 bg-white/60 rounded-lg p-4">
            {data.footer_label && <span className="font-bold">{data.footer_label} </span>}
            {data.footer_body}
          </p>
        </div>
      )}
    </div>
  </div>
);

const FeatureGrid = ({ data }) => {
  const cols = data.columns === 2 ? 'md:grid-cols-2' : data.columns === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';
  return (
    <div className="max-w-6xl mx-auto px-6">
      <div className={`grid ${cols} gap-8`}>
        {(data.items || []).map((item, idx) => (
          <div key={idx} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
            {item.icon && (
              <div className="flex justify-center mb-4">
                <Icon name={item.icon} className="text-[#B8956A]" size={36} />
              </div>
            )}
            <h3 className="text-xl font-bold mb-3 text-center" style={{ fontFamily: 'var(--font-serif)' }}>
              {item.title}
            </h3>
            <p className="text-gray-700 text-center">{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const BulletList = ({ data }) => (
  <div>
    {data.title && (
      <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
        {data.title}
      </h3>
    )}
    <ul className="space-y-4 text-gray-700">
      {(data.items || []).map((item, idx) => (
        <li key={idx} className="flex items-start">
          <span className="text-[#B8956A] mr-3 text-xl">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const DetailsGrid = ({ data }) => (
  <div>
    {data.title && (
      <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
        {data.title}
      </h3>
    )}
    <div className="space-y-5 text-gray-700">
      {(data.items || []).map((item, idx) => (
        <div key={idx}>
          <p className="font-semibold text-[#B8956A]">{item.label}</p>
          <p>{item.value}</p>
        </div>
      ))}
    </div>
  </div>
);

const renderInline = (block) => {
  if (!block) return null;
  if (block.type === 'bullet_list') return <BulletList data={block} />;
  if (block.type === 'details_grid') return <DetailsGrid data={block} />;
  return null;
};

const TwoColumn = ({ data }) => (
  <div className="max-w-5xl mx-auto px-6">
    <div className="bg-white rounded-2xl p-10 md:p-14 shadow-2xl">
      <div className="grid md:grid-cols-2 gap-12">
        <div>{renderInline(data.left)}</div>
        <div>{renderInline(data.right)}</div>
      </div>
    </div>
  </div>
);

const CtaButton = ({ data }) => {
  const isReserve = data.link && /resy\.com/i.test(data.link);
  const handleClick = () => {
    if (isReserve) {
      openReservation(data.link, 'cta-button');
      return;
    }
    if (data.link?.startsWith('mailto:') || data.link?.startsWith('tel:')) {
      track('Contact', { content_name: data.text || 'Contact', location: 'cta-button' });
      window.location.href = data.link;
    } else {
      window.open(data.link || '#', data.link?.startsWith('http') ? '_blank' : '_self');
    }
  };
  return (
    <div className="text-center px-6">
      <button
        onClick={handleClick}
        className="bg-[#1a1a1a] text-white px-10 md:px-12 py-4 text-sm tracking-[0.15em] uppercase hover:bg-black transition-all duration-300 inline-flex items-center justify-center gap-2"
        data-testid="section-cta-button"
      >
        {isReserve && <LucideIcons.Calendar size={16} />}
        {data.text || 'Reserve'}
      </button>
      {data.note && <p className="text-sm text-gray-600 mt-4">{data.note}</p>}
    </div>
  );
};

const InitiativesGrid = ({ data }) => {
  const [items, setItems] = useState([]);
  const icons = ['Heart', 'GraduationCap', 'Users', 'Sprout'];
  useEffect(() => {
    if (data.source === 'community') {
      contentAPI.getCommunity().then((d) => setItems(d || []));
    }
  }, [data.source]);
  return (
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid md:grid-cols-2 gap-8">
        {items.map((it, idx) => (
          <div key={idx} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-l-4 border-[#C9A86C]">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-gradient-to-br from-[#C9A86C] to-[#D4A574] rounded-full">
                <Icon name={icons[idx % icons.length]} className="text-white" size={28} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                  {it.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">{it.description}</p>
                <div className="inline-block bg-[#C9A86C] text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {it.impact}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MissionCard = ({ data }) => (
  <div className="max-w-6xl mx-auto px-6">
    <div className="bg-gradient-to-r from-[#F5EBE0] to-[#E8C9A0] rounded-2xl p-12 text-center shadow-xl">
      {data.title && (
        <h3 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.title}
        </h3>
      )}
      {data.body && <p className="text-lg text-gray-800 mb-8 max-w-2xl mx-auto leading-relaxed">{data.body}</p>}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        {data.primary_text && (
          <button
            onClick={() => window.open(data.primary_link || '#', '_blank')}
            className="bg-[#1a1a1a] text-white px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-black transition-all duration-300"
          >
            {data.primary_text}
          </button>
        )}
        {data.secondary_text && (
          <a
            href={data.secondary_link || '#'}
            className="border-2 border-[#1a1a1a] text-[#1a1a1a] px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-[#1a1a1a] hover:text-white transition-all duration-300"
          >
            {data.secondary_text}
          </a>
        )}
      </div>
    </div>
  </div>
);

const InfoCardGrid = ({ data }) => {
  const cols = data.columns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3';
  return (
    <div className="max-w-7xl mx-auto px-6">
      {data.title && (
        <h2 className="text-5xl text-center mb-16" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.title}
        </h2>
      )}
      <div className={`grid ${cols} gap-8`}>
        {(data.items || []).map((event, idx) => (
          <div key={idx} className="bg-[#FFFDF9] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-[#C9A86C]">
            <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
              {event.name}
            </h3>
            {event.description && <p className="text-gray-700 mb-4">{event.description}</p>}
            {event.capacity && <p className="text-[#B8956A] font-semibold mb-4">{event.capacity}</p>}
            <ul className="space-y-2">
              {(event.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#B8956A] mt-1">•</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const InfoCardGridPackages = ({ data }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    contentAPI.getPrivateEvents().then((d) => setItems(d || []));
  }, []);
  return (
    <div className="max-w-6xl mx-auto px-6">
      {data.title && (
        <h2 className="text-5xl text-center mb-16" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.title}
        </h2>
      )}
      <div className="grid md:grid-cols-2 gap-8">
        {items.map((pkg, idx) => (
          <div key={idx} className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300">
            <h3 className="text-2xl font-bold mb-3 text-[#B8956A]" style={{ fontFamily: 'var(--font-serif)' }}>
              {pkg.name}
            </h3>
            <p className="text-gray-700 leading-relaxed">{pkg.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const VenueCard = ({ data }) => (
  <div className="max-w-5xl mx-auto px-6">
    <div className="bg-gradient-to-br from-[#F5EBE0] to-[#E8C9A0] rounded-2xl p-12 shadow-2xl">
      {data.name && (
        <h2 className="text-4xl mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.name}
        </h2>
      )}
      {data.capacity && <p className="text-xl mb-6 font-semibold">{data.capacity}</p>}
      <ul className="grid md:grid-cols-2 gap-4">
        {(data.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <Icon name="Sparkles" className="text-[#B8956A] flex-shrink-0 mt-1" size={20} />
            <span className="text-gray-800">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const ContactBlock = ({ data }) => {
  const [info, setInfo] = useState({});
  useEffect(() => {
    if (data.source === 'restaurant_info') {
      contentAPI.getRestaurantInfo().then((d) => setInfo(d || {}));
    }
  }, [data.source]);
  return (
    <div>
      {data.title && (
        <h2 className="text-3xl mb-8" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.title}
        </h2>
      )}
      <div className="space-y-8">
        {info.address && (
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#F5EBE0] rounded-full">
              <Icon name="MapPin" className="text-[#B8956A]" size={24} />
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-1">Location</h4>
              <p className="text-gray-700">{info.address}</p>
            </div>
          </div>
        )}
        {info.phone && (
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#F5EBE0] rounded-full">
              <Icon name="Phone" className="text-[#B8956A]" size={24} />
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-1">Phone</h4>
              <a href={`tel:${info.phone}`} className="text-gray-700 hover:text-[#B8956A] transition-colors">
                {info.phone}
              </a>
            </div>
          </div>
        )}
        {info.email && (
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#F5EBE0] rounded-full">
              <Icon name="Mail" className="text-[#B8956A]" size={24} />
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-1">Email</h4>
              <a href={`mailto:${info.email}`} className="text-gray-700 hover:text-[#B8956A] transition-colors">
                {info.email}
              </a>
            </div>
          </div>
        )}
        {info.hours_weekdays && (
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#F5EBE0] rounded-full">
              <Icon name="Clock" className="text-[#B8956A]" size={24} />
            </div>
            <div>
              <h4 className="font-semibold text-lg mb-1">Hours</h4>
              <p className="text-gray-700">{info.hours_label || 'Wednesday - Sunday'}: {info.hours_weekdays}</p>
              {info.hours_closed_note && (
                <p className="text-gray-500 text-sm mt-1">{info.hours_closed_note}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MapEmbed = ({ data }) => (
  <div className="rounded-xl overflow-hidden shadow-lg">
    <iframe
      title={data.title || 'Location'}
      src={data.src}
      width="100%"
      height={data.height || 300}
      style={{ border: 0 }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      className="w-full"
    />
  </div>
);

const ReservationCard = ({ data }) => {
  const [info, setInfo] = useState({});
  useEffect(() => {
    contentAPI.getRestaurantInfo().then((d) => setInfo(d || {}));
  }, []);
  return (
    <div>
      {data.title && (
        <h2 className="text-3xl mb-8" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.title}
        </h2>
      )}
      <div className="bg-gradient-to-br from-[#F5EBE0] to-[#E8C9A0] rounded-xl p-8 shadow-lg">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center mb-8">
            <h4 className="text-2xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
              {data.heading}
            </h4>
            {data.body && <p className="text-gray-700">{data.body}</p>}
          </div>
          <div className="space-y-4">
            <button
              onClick={() => openReservation(data.primary_link || null, 'contact-reservation-card')}
              className="w-full bg-[#1a1a1a] text-white py-4 text-sm tracking-[0.15em] uppercase hover:bg-black transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              <LucideIcons.Calendar size={16} /> {data.primary_text}
            </button>
            {info.phone && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">or</p>
                <a href={`tel:${info.phone}`} className="text-[#B8956A] font-semibold hover:underline">
                  {data.phone_label || 'Call us:'} {info.phone}
                </a>
              </div>
            )}
          </div>
          {(data.tips || []).length > 0 && (
            <div className="mt-8 p-4 bg-[#F5EBE0] rounded-lg">
              <h5 className="font-semibold mb-3">{data.tips_title || 'Reservation Tips:'}</h5>
              <ul className="text-sm text-gray-700 space-y-2">
                {data.tips.map((t, i) => (
                  <li key={i}>• {t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MenuItemsList = ({ data }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    contentAPI.getMenu().then((d) => {
      if (d && data.category) setItems(d[data.category] || []);
    });
  }, [data.category]);
  return (
    <div className="max-w-4xl mx-auto px-6">
      <div className="space-y-12">
        {items.map((item, idx) => (
          <div key={idx} className="text-center border-b border-gray-200 pb-12 last:border-b-0">
            <h3 className="text-2xl md:text-3xl font-medium mb-4 tracking-wide" style={{ fontFamily: 'var(--font-serif)', color: '#1a1a1a' }}>
              {item.name}
              {item.special && (
                <span className="ml-3 text-xs font-normal tracking-wider uppercase bg-[#C9A86C] text-white px-3 py-1 rounded">
                  {item.special}
                </span>
              )}
            </h3>
            {item.description && (
              <p className="text-gray-600 text-base md:text-lg leading-relaxed tracking-wide max-w-2xl mx-auto">
                {item.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Note = ({ data }) => (
  <div className="text-center py-8 px-6">
    <p className="text-gray-600 text-sm leading-relaxed max-w-lg mx-auto whitespace-pre-line">
      {data.text}
    </p>
  </div>
);

const PricingCard = ({ data }) => {
  const tiers = data.tiers || [];
  return (
    <div className="max-w-5xl mx-auto px-6">
      {data.title && (
        <h2
          className="font-light text-center mb-4 leading-[0.95] lowercase tracking-tight"
          style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2.25rem, 5vw, 3.25rem)' }}
        >
          {data.title}
        </h2>
      )}
      {data.subtitle && (
        <p className="text-center text-gray-600 italic mb-12 max-w-2xl mx-auto" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.subtitle}
        </p>
      )}
      <div className={`grid gap-8 ${tiers.length === 1 ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
        {tiers.map((tier, idx) => (
          <div
            key={idx}
            className={`bg-white border-t-2 ${idx === 0 ? 'border-[#0a0a0a]' : 'border-[#C9A86C]'} p-8 md:p-10 shadow-sm`}
            data-testid={`pricing-tier-${idx}`}
          >
            <div className="flex items-baseline justify-between gap-4 mb-2">
              <h3
                className="font-normal leading-snug lowercase"
                style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.5rem, 3vw, 1.875rem)' }}
              >
                {tier.name}
              </h3>
              <div className="text-right">
                {tier.price_prefix && <span className="text-sm text-gray-500 mr-1">{tier.price_prefix}</span>}
                <span
                  className="font-normal tabular-nums"
                  style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.5rem, 3vw, 1.875rem)' }}
                >
                  {tier.price}
                </span>
                {tier.price_suffix && <span className="text-sm text-gray-500 ml-1">{tier.price_suffix}</span>}
              </div>
            </div>
            {tier.per_unit && <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">{tier.per_unit}</p>}
            {tier.description && (
              <p className="text-gray-700 leading-relaxed mb-4 whitespace-pre-line">{tier.description}</p>
            )}
            {tier.inclusions && tier.inclusions.length > 0 && (
              <ul className="space-y-2 mt-5 text-sm text-gray-700">
                {tier.inclusions.map((line, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="text-[#C9A86C] mt-1.5 flex-shrink-0">•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            )}
            {tier.options && tier.options.length > 0 && (
              <ul className="space-y-3 mt-5 border-t border-gray-100 pt-5">
                {tier.options.map((opt, i) => (
                  <li key={i} className="text-sm text-gray-700">
                    {opt.label && <span className="font-medium text-gray-900">{opt.label}</span>}
                    {opt.label && opt.description && <span className="text-gray-500"> — </span>}
                    {opt.description && <span>{opt.description}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      {data.footnote && (
        <p className="text-center text-xs text-gray-500 mt-10 max-w-3xl mx-auto leading-relaxed">{data.footnote}</p>
      )}
      {data.info_row && data.info_row.length > 0 && (
        <div className="mt-12 pt-8 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {data.info_row.map((info, i) => (
            <div key={i} className="text-center">
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#C9A86C] mb-2">{info.label}</div>
              <div className="text-gray-800 text-sm leading-snug">{info.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HtmlBlock = ({ data }) => (
  <div className="max-w-4xl mx-auto px-6 prose prose-lg" dangerouslySetInnerHTML={{ __html: data.html || '' }} />
);

const SectionRenderer = ({ section, pageSlug }) => {
  if (!section || !section.type) return null;
  const data = section.data || {};
  const wrapper = (children) => (
    <div className="py-12 md:py-16" data-testid={`section-${section.type}`}>
      {children}
    </div>
  );
  switch (section.type) {
    case 'rich_text':
      return wrapper(<RichText data={data} />);
    case 'card_with_image':
      return wrapper(<CardWithImage data={data} />);
    case 'feature_grid':
      return wrapper(<FeatureGrid data={data} />);
    case 'two_column':
      return wrapper(<TwoColumn data={data} />);
    case 'bullet_list':
      return wrapper(<div className="max-w-4xl mx-auto px-6"><BulletList data={data} /></div>);
    case 'details_grid':
      return wrapper(<div className="max-w-4xl mx-auto px-6"><DetailsGrid data={data} /></div>);
    case 'cta_button':
      return wrapper(<CtaButton data={data} />);
    case 'initiatives_grid':
      return wrapper(<InitiativesGrid data={data} />);
    case 'mission_card':
      return wrapper(<MissionCard data={data} />);
    case 'info_card_grid':
      return wrapper(<InfoCardGrid data={data} />);
    case 'info_card_grid_packages':
      return wrapper(<InfoCardGridPackages data={data} />);
    case 'venue_card':
      return wrapper(<VenueCard data={data} />);
    case 'inquiry_form':
      return wrapper(<InquiryForm data={data} pageSlug={pageSlug} />);
    case 'contact_block':
      return wrapper(<div className="max-w-4xl mx-auto px-6"><ContactBlock data={data} /></div>);
    case 'map_embed':
      return wrapper(<div className="max-w-4xl mx-auto px-6"><MapEmbed data={data} /></div>);
    case 'reservation_card':
      return wrapper(<div className="max-w-4xl mx-auto px-6"><ReservationCard data={data} /></div>);
    case 'menu_items_list':
      return wrapper(<MenuItemsList data={data} />);
    case 'note':
      return wrapper(<Note data={data} />);
    case 'pricing_card':
      return wrapper(<PricingCard data={data} />);
    case 'html':
      return wrapper(<HtmlBlock data={data} />);
    default:
      return null;
  }
};

export default SectionRenderer;
